import { useEffect, useState } from 'react';

export interface City {
  id: string;
  label: string;
  lat: number;
  lon: number;
  timeZone: string; // IANA
}

// Five cities in priority order. "Utah" → Salt Lake City coords + Mountain Time.
export const CITIES: City[] = [
  { id: 'tampa', label: 'Tampa', lat: 27.95, lon: -82.46, timeZone: 'America/New_York' },
  { id: 'utah', label: 'Utah', lat: 40.76, lon: -111.89, timeZone: 'America/Denver' },
  { id: 'chicago', label: 'Chicago', lat: 41.88, lon: -87.63, timeZone: 'America/Chicago' },
  { id: 'london', label: 'London', lat: 51.51, lon: -0.13, timeZone: 'Europe/London' },
  { id: 'kolkata', label: 'Kolkata', lat: 22.57, lon: 88.36, timeZone: 'Asia/Kolkata' },
];

const TICK_MS = 15_000;
const WEATHER_MS = 30 * 60_000; // 30 minutes

const WEATHER_URL =
  'https://api.open-meteo.com/v1/forecast' +
  `?latitude=${CITIES.map((c) => c.lat).join(',')}` +
  `&longitude=${CITIES.map((c) => c.lon).join(',')}` +
  '&current=temperature_2m&temperature_unit=fahrenheit';

export function formatCityTime(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

export default function CityStrip() {
  const [now, setNow] = useState(() => new Date());
  const [temps, setTemps] = useState<Record<string, number | null>>({});

  // Real-time clock: re-render every 15s so the minute field stays current.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Temperatures: one batched Open-Meteo call on mount, then every 30 min.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(WEATHER_URL);
        if (!res.ok) throw new Error(`weather ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : [data];
        if (cancelled) return;
        setTemps((prev) => {
          const next = { ...prev };
          CITIES.forEach((c, i) => {
            const t = list[i]?.current?.temperature_2m;
            if (typeof t === 'number') next[c.id] = Math.round(t);
          });
          return next;
        });
      } catch {
        // Keep last-known temps; the clock is unaffected.
      }
    };
    load();
    const id = setInterval(load, WEATHER_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <section className="citystrip" aria-label="City times and weather">
      {CITIES.map((c) => (
        <div key={c.id} className="citystrip__cell">
          <span className="citystrip__label">{c.label}</span>
          <span className="citystrip__time">{formatCityTime(now, c.timeZone)}</span>
          <span className="citystrip__temp">
            {temps[c.id] == null ? '—' : `${temps[c.id]}°F`}
          </span>
        </div>
      ))}
    </section>
  );
}
